import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link as MuiLink,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Grid,
  Fade,
  Backdrop,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Person } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useAuth } from '../components/context/AuthContext';

// Custom MUI Theme (aligned with LoginPage)
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      dark: '#115293',
      contrastText: '#ffffff',
    },
    background: {
      default: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    body2: {
      fontSize: '0.9rem',
      lineHeight: 1.5,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          padding: '10px 16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            transition: 'all 0.2s ease',
            '&:hover fieldset': {
              borderColor: '#1976d2',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#115293',
              boxShadow: '0 0 8px rgba(25, 118, 210, 0.3)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.97)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
        },
      },
    },
  },
});

const RegisterPage = React.memo(() => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const { register, verifyOtp, error, setError } = useAuth();
  const navigate = useNavigate();

  const validateForm = useCallback(() => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.name.trim() || formData.name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    if (!emailRegex.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    } else if (!/[A-Z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      errors.password = 'Password must include an uppercase letter and a number';
    }
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
      if (error) setError(null);
      if (otpMessage) setOtpMessage('');
    },
    [error, setError, otpMessage]
  );

  const handleOtpChange = useCallback(
    (e) => {
      setOtp(e.target.value);
      if (otpMessage) setOtpMessage('');
    },
    [otpMessage]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateForm()) return;

      setLoading(true);
      const result = await register(formData.name, formData.email, formData.password);
      setLoading(false);

      if (result.success) {
        setOtpStep(true);
        setOtpMessage(result.message || 'OTP sent to your email. Please verify.');
      }
    },
    [formData, register, validateForm]
  );

  const handleOtpSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!otp.trim()) {
        setOtpMessage('Please enter the OTP');
        return;
      }

      setOtpLoading(true);
      const result = await verifyOtp(formData.email, otp);
      setOtpLoading(false);

      if (result.success) {
        setOtpMessage('Registration complete! Redirecting to login...');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        setOtpMessage(result.message || 'Invalid or expired OTP');
        setOtp('');
      }
    },
    [otp, formData.email, verifyOtp, navigate]
  );

  const handleResendOtp = useCallback(async () => {
    setOtpLoading(true);
    const result = await register(formData.name, formData.email, formData.password);
    setOtpLoading(false);
    if (result.success) {
      setOtpMessage('OTP resent to your email. Please verify.');
      setOtp('');
    } else {
      setOtpMessage(result.message || 'Failed to resend OTP');
    }
  }, [formData, register]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: theme.palette.background.default,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Container maxWidth="xs">
          <Fade in timeout={800}>
            <Paper elevation={12} sx={{ p: { xs: 3, sm: 4 } }}>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
                <Typography
                  variant="h5"
                  component="h1"
                  sx={{ fontSize: { xs: '1.8rem', sm: '2rem', md: '2.25rem' } }}
                >
                  Create Account
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: 'text.secondary' }}
                >
                  Join us to start sending emails
                </Typography>
              </Box>

              {/* Error or Info Alert */}
              {(error || Object.keys(formErrors).length > 0 || otpMessage) && (
                <Fade in timeout={300}>
                  <Alert
                    severity={otpStep && !error && !Object.keys(formErrors).length ? 'info' : 'error'}
                    sx={{ mb: 3, borderRadius: '8px' }}
                  >
                    {otpMessage || error || Object.values(formErrors)[0]}
                  </Alert>
                </Fade>
              )}

              {/* Register Form or OTP Form */}
              {!otpStep ? (
                <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        size="medium"
                        error={!!formErrors.name}
                        helperText={formErrors.name}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{ 'aria-label': 'Full name' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        size="medium"
                        error={!!formErrors.email}
                        helperText={formErrors.email}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{ 'aria-label': 'Email address' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        required
                        size="medium"
                        error={!!formErrors.password}
                        helperText={formErrors.password}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={togglePasswordVisibility}
                                edge="end"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{ 'aria-label': 'Password' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Confirm Password"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        size="medium"
                        error={!!formErrors.confirmPassword}
                        helperText={formErrors.confirmPassword}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={toggleConfirmPasswordVisibility}
                                edge="end"
                                aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{ 'aria-label': 'Confirm password' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading || Object.keys(formErrors).length > 0}
                        sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
                      >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleOtpSubmit} sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Enter OTP"
                        name="otp"
                        type="text"
                        value={otp}
                        onChange={handleOtpChange}
                        required
                        size="medium"
                        inputProps={{ 'aria-label': 'OTP code' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={otpLoading}
                        sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
                      >
                        {otpLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="text"
                        onClick={handleResendOtp}
                        disabled={otpLoading}
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          color: 'primary.main',
                          textTransform: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Resend OTP
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Footer */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Already have an account?{' '}
                  <MuiLink
                    component={RouterLink}
                    to="/login"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Sign in
                  </MuiLink>
                </Typography>
                <MuiLink
                  component={RouterLink}
                  to="/"
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  ← Back to Home
                </MuiLink>
              </Box>
            </Paper>
          </Fade>
        </Container>

        {/* Loading Backdrop */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading || otpLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </ThemeProvider>
  );
});

export default RegisterPage;